// ANALYSIS AND CHANGES:
// 1. CARD LAYOUT: classes were added to `CardForm` to cap its width on large screens (`max-w-4xl`) and center it (`mx-auto`), matching the requested 30% reduction. The `w-full` class keeps it responsive on smaller screens.
// 2. FORM SPACING: added the `gap-y-4` class to the form grid to give consistent vertical spacing between fields.
// 3. FONT STANDARDIZATION: applied the `font-azonix` and `font-coda` classes to titles and labels to align with the project design system.

import { api } from '@common';
import {
  CardForm,
  InputMask,
  RadioGender,
  WrapperPage,
} from '@components/atoms';
import { maskUSPhone } from '@helpers';
import { useAuth } from '@hooks';
import {
  Button,
  DatePicker,
  Form,
  Input,
  message,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

const { Title } = Typography;

export const SettingsPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { profile } = useAuth();

  const [form] = Form.useForm();

  const { data, isLoading: isLoadingProfile, refetch } = profile;

  const updateProfile = (formData: any) => {
    setIsLoading(true);
    // Convert the dayjs object back into a YYYY-MM-DD string before sending
    const payload = {
      ...formData,
      birthday: formData.birthday ? formData.birthday.format('YYYY-MM-DD') : null,
    };
    api
      .patch('/users/profile', payload)
      .then(() => {
        message.success('Profile updated');
        refetch();
      })
      .catch(error => {
        console.error('Error updating profile:', error);
        message.error('Failed to update profile');
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (!isLoadingProfile && data) {
      const formValues = {
        ...data?.physicalPerson,
        birthday: data?.physicalPerson?.birthday
          ? dayjs(data?.physicalPerson?.birthday) // dayjs vai parsear a string
          : null,
        phone: data?.physicalPerson?.phone ? maskUSPhone(data?.physicalPerson?.phone) : null,
      };
      form.setFieldsValue(formValues);
    }
  }, [data, isLoadingProfile, form]);

  return (
    <div className="estabrook-bg pt-25 pb-10 md:pb-20 justify-center">
      <WrapperPage isLoading={isLoading || isLoadingProfile}>
        <div className="container h-full flex flex-col justify-center items-center mx-auto">
            <Title level={1} className="font-azonix !font-normal text-primary tracking-wider !my-15"
            >SETTINGS
            </Title>
        </div>

        <CardForm className="w-full max-w-4xl mx-auto">
          <Typography.Title level={3} className='font-azonix !font-normal text-primary'>Personal Information</Typography.Title>

          <Form
            size='large'
            layout='vertical'
            form={form}
            onFinish={updateProfile}
            className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4'
          >
            <Form.Item label={<span className="font-coda">Name</span>} name='name' rules={[{ required: true, message: "Please enter your name" }]}>
              <Input placeholder='Enter name' />
            </Form.Item>

            <Form.Item label={<span className="font-coda">Date of Birth</span>} name='birthday'>
              <DatePicker
                className='w-full'
                format="MM/DD/YYYY"
                placeholder='MM/DD/YYYY'
              />
            </Form.Item>

            <Form.Item label={<span className="font-coda">Phone</span>} name='phone' >
              <InputMask
                mask="usphone" placeholder='(000) 000-0000' />
            </Form.Item>

            <Form.Item
              label={<span className="font-coda">Gender</span>}
              name='gender'
            >
              <RadioGender />
            </Form.Item>

            <Form.Item label={<span className="font-coda">Profession</span>} name='profession'>
              <Input placeholder='Enter profession' />
            </Form.Item>

            <div className='col-span-1 md:col-span-2 flex justify-center my-4'>
              <Button type='primary' className='btn-double-cut !w-fit' htmlType='submit'>
                Update Profile
              </Button>
            </div>
          </Form>
        </CardForm>
      </WrapperPage>
    </div>
  );
};
